import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CssBaseline from '@mui/material/CssBaseline';
import FormControlLabel from '@mui/material/FormControlLabel';
import Divider from '@mui/material/Divider';
import FormLabel from '@mui/material/FormLabel';
import FormControl from '@mui/material/FormControl';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import MuiCard from '@mui/material/Card';
import { styled } from '@mui/material/styles';
import ForgotPassword from '../../components/sign-in/ForgotPassword';
import AppTheme from '../../components/shared-theme/AppTheme';
import InputAdornment from '@mui/material/InputAdornment';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import DynamicsLogo from '../../components/images/DynamicsLogo';
import { useNavigate, useLocation } from 'react-router-dom';
import TransitionMark from '../Transitions/TransitionMark';
import { sendWhatsAppAuth, sendEmailAuth } from '../../utils/api';
import CircularProgress from '@mui/material/CircularProgress';



const Card = styled(MuiCard)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignSelf: 'center',
  width: '100%',
  padding: theme.spacing(4),
  gap: theme.spacing(2),
  margin: 'auto',
  [theme.breakpoints.up('sm')]: {
    maxWidth: '450px',
  },
  boxShadow:
    'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',
  ...theme.applyStyles('dark', {
    boxShadow:
      'hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px',
  }),
}));

const SignInContainer = styled(Stack)(({ theme }) => ({
  height: 'calc((1 - var(--template-frame-height, 0)) * 100dvh)',
  minHeight: '100%',
  padding: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(4),
  },
  '&::before': {
    content: '""',
    display: 'block',
    position: 'absolute',
    zIndex: -1,
    inset: 0,
    backgroundImage:
      'radial-gradient(ellipse at 50% 50%, hsl(210, 100%, 97%), hsl(0, 0%, 100%))',
    backgroundRepeat: 'no-repeat',
    ...theme.applyStyles('dark', {
      backgroundImage:
        'radial-gradient(at 50% 50%, hsla(210, 100%, 16%, 0.5), hsl(220, 30%, 5%))',
    }),
  },
}));

export default function SignIn(props) {
  const navigate = useNavigate();
  const [emailError, setEmailError] = React.useState(false);
  const [emailErrorMessage, setEmailErrorMessage] = React.useState('');
  const [passwordError, setPasswordError] = React.useState(false);
  const [passwordErrorMessage, setPasswordErrorMessage] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const [showPassword, setShowPassword] = React.useState(false);
  const [isWhatsappSignIn, setIsWhatsappSignIn] = React.useState(false);
  const [whatsappValue, setWhatsappValue] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [emailValue, setEmailValue] = useState('');
  const [passwordValue, setPasswordValue] = useState('');


  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleWhatsappChange = (event) => {
    let value = event.target.value;

    // Remove any non-numeric characters
    value = value.replace(/[^0-9]/g, '');

    // Ensure the first two characters are '08'
    if (value.length > 0 && !value.startsWith('08')) {
      value = '08' + value.slice(2);
    }

    setWhatsappValue(value);
  };

  const handleEmailChange = (event) => {
    setEmailValue(event.target.value);
  };

  const handlePasswordChange = (event) => {
    setPasswordValue(event.target.value);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (validateInputs()) {
      setLoading(true);
      const data = new FormData(event.currentTarget);
      try {
        if (isWhatsappSignIn) {
          // WhatsApp Login
          const whatsapp = data.get('whatsapp');
          const response = await sendWhatsAppAuth(whatsapp);
          console.log('WhatsApp authentication response:', response);

          // Navigate to OTP page with login-whatsapp method
          navigate('/otp', {
            state: {
              method: 'login-whatsapp', // More specific method
              contact: whatsapp
            }
          });
        } else {
          // Email Login
          const email = data.get('email');
          const password = data.get('password');
          const response = await sendEmailAuth(email, password);
          console.log('Email authentication response:', response);

          // Navigate to OTP page with login-email method
          navigate('/otp', {
            state: {
              method: 'login-email', // More specific method
              contact: email,
              password: password
            }
          });
        }
      } catch (error) {
        console.error('Login error:', error);
        setEmailError(true);
        setEmailErrorMessage('Failed to authenticate. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  const validateInputs = () => {
    const email = document.getElementById('email');
    const password = document.getElementById('password');
    const whatsapp = document.getElementById('whatsapp');

    let isValid = true;

    if (!isWhatsappSignIn) { // Only validate email and password if not signing in with WhatsApp
      if (!email.value || !/\S+@\S+\.\S+/.test(email.value)) {
        setEmailError(true);
        setEmailErrorMessage('Please enter a valid email address.');
        isValid = false;
      } else {
        setEmailError(false);
        setEmailErrorMessage('');
      }

      if (!password.value || password.value.length < 6) {
        setPasswordError(true);
        setPasswordErrorMessage('Password must be at least 6 characters long.');
        isValid = false;
      } else {
        setPasswordError(false);
        setPasswordErrorMessage('');
      }
    } else {
      // WhatsApp validation
      if (!whatsapp.value || !/^\d{10,15}$/.test(whatsapp.value)) {
        // Regex to validate 10-15 digit phone number
        setEmailError(true);  // Reuse emailError state to show error message for WhatsApp
        setEmailErrorMessage('Mohon periksa kembali nomor Anda.');
        isValid = false;
      } else {
        setEmailError(false);
        setEmailErrorMessage('');
      }
    }

    return isValid;
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const resetErrors = () => {
    // Reset error states
    setEmailError(false);
    setEmailErrorMessage('');
    setPasswordError(false);
    setPasswordErrorMessage('');
  };

  const handleWhatsappSignIn = () => {
    resetErrors(); // Reset errors when switching to WhatsApp sign-in
    setIsWhatsappSignIn(true);
  };

  const handleEmailSignIn = () => {
    resetErrors(); // Reset errors when switching to email sign-in
    setIsWhatsappSignIn(false);
  };

  const [showTransition, setShowTransition] = useState(false);
  const location = useLocation();

  useEffect(() => {
    // Trigger transition on route change
    setShowTransition(true);
    const timer = setTimeout(() => setShowTransition(false), 2000); // Show for 2 seconds
    return () => clearTimeout(timer); // Clean up timeout on unmount
  }, [location]);

  return (
    <AppTheme {...props}>
      <CssBaseline enableColorScheme />
      {showTransition && <TransitionMark />}
      {!showTransition && (
        <>
          <SignInContainer direction="column" justifyContent="space-between">
            <Card variant="outlined">
              <DynamicsLogo height="34px" />
              <Typography
                component="h1"
                variant="h4"
                sx={{ width: '100%', fontSize: 'clamp(1.5rem, 10vw, 1.15rem)' }}
              >
                Sign in
              </Typography>
              <Box
                component="form"
                onSubmit={handleSubmit}
                noValidate
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: '100%',
                  gap: 2,
                }}
              >
                {isWhatsappSignIn ? (
                  // WhatsApp sign-in form
                  <FormControl>
                    <FormLabel htmlFor="whatsapp">Nomor whatsapp</FormLabel>
                    <TextField
                      id="whatsapp"
                      type="tel"
                      name="whatsapp"
                      placeholder="Nomor Whatsapp"
                      required
                      fullWidth
                      variant="outlined"
                      size="small"
                      inputMode="tel"
                      autoFocus
                      value={whatsappValue}
                      onChange={handleWhatsappChange}
                      error={emailError}
                      helperText={emailErrorMessage}
                      slotProps={{
                        input: {
                          maxLength: 13, // Optional: Limit the input length
                        },
                      }}
                    />
                  </FormControl>
                ) : (
                  // Regular email/password form
                  <>
                    <FormControl>
                      <FormLabel htmlFor="email">Email</FormLabel>
                      <TextField
                        error={emailError}
                        helperText={emailErrorMessage}
                        id="email"
                        type="email"
                        name="email"
                        placeholder="your@email.com"
                        autoComplete="email"
                        autoFocus={isWhatsappSignIn === false} // Autofocus email when signing in with email
                        required
                        fullWidth
                        variant="outlined"
                        color={emailError ? 'error' : 'primary'}
                        size="small"
                        value={emailValue} // Controlled value
                        onChange={handleEmailChange} // Controlled onChange handler
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel htmlFor="password">Password</FormLabel>
                      <TextField
                        error={passwordError}
                        helperText={passwordErrorMessage}
                        name="password"
                        placeholder="••••••"
                        type={showPassword ? 'text' : 'password'}
                        id="password"
                        autoComplete="current-password"
                        required
                        fullWidth
                        variant="outlined"
                        color={passwordError ? 'error' : 'primary'}
                        size="small"
                        slotProps={{
                          input: {
                            endAdornment: (
                              <InputAdornment position="end">
                                <Button
                                  variant="text"
                                  onClick={handleClickShowPassword}
                                  size="small"
                                  sx={{
                                    backgroundColor: 'transparent',
                                    minWidth: 0,
                                    padding: 0,
                                    '&:hover': {
                                      backgroundColor: 'transparent',
                                    },
                                  }}
                                >
                                  {showPassword ? <VisibilityOff /> : <Visibility />}
                                </Button>
                              </InputAdornment>
                            ),
                          },
                        }}
                        value={passwordValue} // Controlled value
                        onChange={handlePasswordChange} // Controlled onChange handler
                      />
                    </FormControl>
                  </>
                )}
                <FormControlLabel
                  control={<Checkbox value="remember" color="primary" />}
                  label="Remember me"
                />
                <ForgotPassword open={open} handleClose={handleClose} signInMethod={isWhatsappSignIn ? 'whatsapp' : 'email'} />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading}
                >
                  {loading ? <Box sx={{ display: 'flex' }}>
                    <CircularProgress size={24} />
                  </Box> : 'Sign in'}
                </Button>
                <Link
                  component="button"
                  type="button"
                  onClick={handleClickOpen}
                  variant="body2"
                  sx={{ alignSelf: 'center' }}
                >
                  Lupa kata sandi?
                </Link>
              </Box>
              <Divider>Atau</Divider>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={isWhatsappSignIn ? handleEmailSignIn : handleWhatsappSignIn}
                >
                  {isWhatsappSignIn ? 'Masuk dengan Email' : 'Masuk dengan Whatsapp'}
                </Button>
                <Typography sx={{ textAlign: 'center' }}>
                  Tidak punya akun?{' '}
                  <Link
                    href="/register"
                    variant="body2"
                    sx={{ alignSelf: 'center' }}
                  >
                    Daftar akun
                  </Link>
                </Typography>
              </Box>
            </Card>
          </SignInContainer>
        </>
      )}
    </AppTheme>
  );
}